import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Highlighter from 'react-highlight-words';
import {
  Breadcrumb,
  Button,
  DatePicker,
  Descriptions,
  Flex,
  Image,
  Input,
  Modal,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  HomeOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import PropTypes from 'prop-types';
import SEO from './SEO';
import compareTwoValues from '../common/compareTwoValues';
import {
  colourPalette,
  dateFormat,
  filterTimeout,
  modalWidth,
  notificationTimeout,
  notificationType,
  requestStatus,
  scrollHeight,
} from '../common/constants';
import debugLog from '../common/debugLog';
import pluralise from '../common/pluralise';
import validateString from '../common/validator';
import {
  db,
  firCheckEmailExists,
  firRejectAccount,
  firRequestAccountInfo,
  primaryAuth,
} from '../firebase';
import './style.scss';

export default function AccountRequests({
  badgeCount,
  site,
  updateRequestCount,
}) {
  const [buttonStatus, setButtonStatus] = useState(); // use to disable button
  const [data, setData] = useState([]); // request data from database
  const [emailExists, setEmailExists] = useState(false); // check duplicate email
  const [filterDate, setFilterDate] = useState({ end: '', start: '' }); // date filter
  const [filterOption, setFilterOption] = useState(requestStatus.pendings); // status
  const [isModalOpen, setIsModalOpen] = useState(false); // modal visibility
  const [loading, setLoading] = useState(false); // loading indicator
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoReason, setInfoReason] = useState('');
  const [rowKeyData, setRowKeyData] = useState({}); // row data
  const [rowKeySelected, setRowKeySelected] = useState([]); // row selection
  const [searchColumn, setSearchColumn] = useState('');
  const [searchText, setSearchText] = useState('');

  const [messageApi, contextHolder] = message.useMessage();

  const searchInput = useRef(null);
  const tableParams = {
    position: ['bottomCenter'],
    showSizeChanger: true,
    showTotal: (total, range) =>
      `${range[0]} - ${range[1]} of ${total} ${pluralise(total, 'request')}`,
    total: data.length,
  };

  // destructure imports
  const { RangePicker } = DatePicker;
  const { Text } = Typography;
  const { antdDefault, antdError, antdSuccess, highlighted, searchActive } =
    colourPalette;
  const { normal } = modalWidth;
  const { timeoutError, timeoutSuccess, timeoutWarning } = notificationTimeout;
  const { typeError, typeSuccess, typeWarning } = notificationType;
  const {
    approved,
    pendings,
    rejected,
    approvedStr,
    pendingsStr,
    rejectedStr,
  } = requestStatus;
  const { id } = site;

  const filterIconColour = (filtered) => (
    <SearchOutlined style={{ color: filtered ? searchActive : undefined }} />
  );

  const filterMenu = (
    clearFilters,
    close,
    confirm,
    dataIndex,
    handleReset,
    handleSearch,
    inputSearch, // searchInput
    selectedKeys,
    // setSearchColumn,
    // setSearchText,
    setSelectedKeys,
  ) => (
    <div className="search-dialog">
      <Input
        id="search-input"
        onChange={(e) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() =>
          handleSearch(true, confirm, dataIndex, selectedKeys)
        }
        placeholder={`Search ${dataIndex}`}
        ref={inputSearch}
        value={selectedKeys[0]}
      />
      <Space>
        <Button
          className="search-button"
          icon={<SearchOutlined />}
          onClick={() => handleSearch(true, confirm, dataIndex, selectedKeys)}
          size="small"
          type="primary"
        >
          Search
        </Button>
        <Button
          className="search-button"
          onClick={() => handleReset(clearFilters, confirm, dataIndex)}
          size="small"
        >
          Reset
        </Button>
        <Button
          onClick={() => handleSearch(false, confirm, dataIndex, selectedKeys)}
          size="small"
          type="link"
        >
          Filter
        </Button>
        <Button onClick={() => close()} size="small" type="link">
          Close
        </Button>
      </Space>
    </div>
  );

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  // D24 (2026-05-14): realtime onSnapshot subscription. Replaces the old
  // one-shot getDocs so new applications appear without a manual page refresh,
  // and reject/approve writes reflect instantly without an imperative reload.
  const subscribeRequests = useCallback(
    (dateFilter, optionFilter, siteId, status) => {
      if (validateString(siteId) === false) {
        console.warn('-- Site ID invalid');
        return () => {};
      }

      setLoading(true);
      updateRequestCount('accountRequests', siteId, status);

      const requestRef = collection(db, 'accountRequests');
      let q = query(
        requestRef,
        where('siteId', '==', siteId),
        where('requestStatus', '==', optionFilter),
      );

      if (dateFilter.start !== '' && dateFilter.end !== '') {
        q = query(
          q,
          where('registrationDate', '>=', dateFilter.start),
          where('registrationDate', '<=', dateFilter.end),
        );
      }
      q = query(q, orderBy('registrationDate', 'desc'));

      const unsub = onSnapshot(
        q,
        (querySnapshot) => {
          const requests = [];
          querySnapshot.forEach((snap) => {
            const dict = snap.data();
            requests.push({
              accountLevel: `${dict.accountType} / ${dict.accessLevel}`,
              applicantRole: dict.applicantRole || dict.accountType || '',
              applicantType: dict.applicantType || 'self',
              applicantUid: dict.applicantUid || '',
              blockNumber: dict.blockNumber,
              contactNumber: dict.contactNumber,
              dateTime: `${dict.registrationDate} ${dict.registrationTime}`,
              decisionReason: dict.decisionReason || '',
              documents: dict.documents || {},
              email: dict.email,
              fullname: dict.fullname,
              requestID: snap.id,
              requestStatus: dict.requestStatus,
              unitNumber: dict.unitNumber,
            });
          });
          setData(requests);
          setLoading(false);
          // Keep the nav badge in sync as docs are added/removed/transitioned.
          updateRequestCount('accountRequests', siteId, status);
        },
        (error) => {
          setData([]);
          setLoading(false);
          console.warn('-- onSnapshot error:', error);
          openMessage('Error subscribing to requests', timeoutWarning, typeWarning);
        },
      );
      return unsub;
    },
    [openMessage, timeoutWarning, typeWarning, updateRequestCount],
  );

  const resetSelection = () => {
    setIsModalOpen(false);
    setRowKeyData({});
    setRowKeySelected([]);
  };

  const handleRequestApprove = () => {
    setLoading(true);

    axios
      .post(
        process.env.REACT_APP_CLOUDRUN_APPROVE_ACCOUNT,
        { requestID: rowKeyData.requestID, uid: primaryAuth.currentUser.uid },
        { headers: { 'Content-Type': 'application/json' } },
      )
      .then((response) => {
        resetSelection();
        setLoading(false);
        debugLog('-- Account successfully created', response.status);
        openMessage(
          'Account successfully created',
          timeoutSuccess,
          typeSuccess,
        );
      })
      .catch((error) => {
        resetSelection();
        setLoading(false);
        console.error('-- Error:', error.message, error);
        openMessage(`Error: ${error.message}`, timeoutError, typeError);
      });
  };

  const handleRequestReject = () => {
    setIsModalOpen(false);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rowKeyData?.requestID) return;
    const reason = rejectReason.trim();
    if (!reason) {
      openMessage('Please enter a rejection reason', timeoutWarning, typeWarning);
      return;
    }
    setLoading(true);
    try {
      // Calls v2rejectAccount callable Function (D24 P0.5). The function
      // updates the doc and applicationNotifier triggers the SendGrid email.
      await firRejectAccount(rowKeyData.requestID, reason);
      setRejectModalOpen(false);
      setRejectReason('');
      resetSelection();
      openMessage('Request rejected — applicant emailed', timeoutSuccess, typeSuccess);
    } catch (error) {
      console.error('-- Error rejecting request:', error);
      openMessage(`Error rejecting: ${error.message}`, timeoutError, typeError);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectCancel = () => {
    setRejectModalOpen(false);
    setRejectReason('');
    resetSelection();
  };

  const handleRequestInfo = () => {
    setIsModalOpen(false);
    setInfoReason('');
    setInfoModalOpen(true);
  };

  const handleInfoConfirm = async () => {
    if (!rowKeyData?.requestID) return;
    const reason = infoReason.trim();
    if (!reason) {
      openMessage(
        'Please describe what additional info is needed',
        timeoutWarning,
        typeWarning,
      );
      return;
    }
    setLoading(true);
    try {
      await firRequestAccountInfo(rowKeyData.requestID, reason);
      setInfoModalOpen(false);
      setInfoReason('');
      resetSelection();
      openMessage(
        'Info request sent — applicant emailed',
        timeoutSuccess,
        typeSuccess,
      );
    } catch (error) {
      console.error('-- Error requesting info:', error);
      openMessage(`Error: ${error.message}`, timeoutError, typeError);
    } finally {
      setLoading(false);
    }
  };

  const handleInfoCancel = () => {
    setInfoModalOpen(false);
    setInfoReason('');
    resetSelection();
  };

  // clear row data and selection then hide the modal
  const handleCancel = () => {
    resetSelection();
    debugLog('-- User cancelled the modal');
  };

  const handleDatePickerChange = (dates) => {
    if (dates === null) {
      setFilterDate({ start: '', end: '' });
    } else {
      const date1 = dayjs(dates[0]).format(dateFormat);
      const date2 = dayjs(dates[1]).format(dateFormat);
      setFilterDate({ start: date1, end: date2 });
    }
  };

  const handleEmailCheck = (email, siteId) => {
    firCheckEmailExists(email, siteId)
      .then((exists) => {
        setEmailExists(exists);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  // Snapshot listener is live — Reload kept for explicit user feedback but
  // it just briefly toggles the loading state; no extra fetch needed.
  const handleReload = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 300);
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    clearFilters();
    confirm({ closeDropdown: false }); // dropdown stays open after searching
    setSearchColumn(dataIndex); // the column to search in
    setSearchText(''); // reset search terms
  };

  const handleSearch = (closeDropdown, confirm, dataIndex, selectedKeys) => {
    confirm({ closeDropdown }); // whether dropdown should close after searching
    setSearchColumn(dataIndex); // the column to search in
    setSearchText(selectedKeys[0]); // the search terms
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      clearFilters,
      close,
      confirm,
      selectedKeys,
      setSelectedKeys,
    }) =>
      filterMenu(
        clearFilters,
        close,
        confirm,
        dataIndex,
        handleReset,
        handleSearch,
        searchInput,
        selectedKeys,
        // setSearchColumn,
        // setSearchText,
        setSelectedKeys,
      ),
    filterIcon: (filtered) => filterIconColour(filtered),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), filterTimeout);
        }
      },
    },
    render: (text) =>
      searchColumn === dataIndex ? (
        <Highlighter
          autoEscape
          highlightStyle={{ backgroundColor: highlighted, padding: 0 }}
          searchWords={[searchText]}
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: 'Status',
      dataIndex: 'requestStatus',
      align: 'center',
      width: 120,
      render: (status) => {
        switch (status) {
          case 0:
            return (
              <Tag
                bordered={false}
                className="table icon-tag"
                color={antdDefault}
                icon={<ClockCircleOutlined />}
              >
                {pendingsStr.toUpperCase()}
              </Tag>
            );
          case 1:
            return (
              <Tag
                bordered={false}
                className="table icon-tag"
                color={antdSuccess}
                icon={<CheckOutlined />}
              >
                {approvedStr.toUpperCase()}
              </Tag>
            );
          case 2:
            return (
              <Tag
                bordered={false}
                className="table icon-tag"
                color={antdError}
                icon={<CloseOutlined />}
              >
                {rejectedStr.toUpperCase()}
              </Tag>
            );
          default:
            return <Tag />;
        }
      },
    },
    {
      title: 'Requested',
      dataIndex: 'dateTime',
      width: 120,
      sortDirections: ['descend', 'ascend'],
      sorter: {
        compare: (a, b) => compareTwoValues(a.dateTime, b.dateTime),
        multiple: 1,
      },
    },
    {
      title: 'Account Level',
      dataIndex: 'accountLevel',
      width: 160,
    },
    {
      title: 'Account Name',
      dataIndex: 'fullname',
      width: 200,
      ...getColumnSearchProps('fullname'),
    },
    {
      title: 'Block',
      dataIndex: 'blockNumber',
      align: 'center',
      width: 100,
    },
    {
      title: 'Unit',
      dataIndex: 'unitNumber',
      align: 'center',
      width: 80,
    },
    {
      title: 'Contact',
      dataIndex: 'contactNumber',
      width: 100,
    },
    {
      title: 'Email Address',
      dataIndex: 'email',
      width: 200,
    },
  ];

  // Documents map<docKind, downloadUrl> from accountRequests/{id}.documents.
  // Mobile clients (D24 P1, both iOS + Android) upload JPEG only — see
  // `encodeJpeg` in cma-android NewAccountViewModel and `ImageResize.jpeg`
  // in cma-ios.  Render every entry as an antd Image thumbnail with
  // built-in click-to-zoom; fallback to a plain anchor only when the URL
  // string suggests a non-image (e.g. an admin manually attached a PDF).
  const isImageUrl = (u) => {
    if (typeof u !== 'string') return false;
    // Strip query params (Firebase Storage adds ?alt=media&token=...).
    const path = u.split('?')[0].toLowerCase();
    return path.endsWith('.jpg') || path.endsWith('.jpeg')
      || path.endsWith('.png') || path.endsWith('.gif')
      || path.endsWith('.webp');
  };
  const renderDocumentBody = (kind, url) => {
    if (!url) return <Text type="secondary">(not uploaded)</Text>;
    if (isImageUrl(url)) {
      return (
        <Image
          src={url}
          alt={kind}
          width={120}
          height={120}
          style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }}
          fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIj48cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+QnJva2VuPC90ZXh0Pjwvc3ZnPg=="
        />
      );
    }
    return (
      <a href={url} rel="noopener noreferrer" target="_blank">
        Open in new tab
      </a>
    );
  };
  const renderDocuments = (docs) => {
    const entries = Object.entries(docs || {});
    if (entries.length === 0) {
      return <Text type="secondary">No documents uploaded.</Text>;
    }
    return (
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {entries.map(([kind, url]) => (
          <Flex key={kind} align="flex-start" gap={12} wrap>
            <Text strong style={{ minWidth: 140 }}>{kind}:</Text>
            {renderDocumentBody(kind, url)}
          </Flex>
        ))}
      </Space>
    );
  };

  const requestItems = (rowData) => [
    {
      key: 'fullname',
      label: 'Account Name',
      children: rowData.fullname,
    },
    {
      key: 'blockUnit',
      label: 'Unit',
      children: `Block ${rowData.blockNumber} #${rowData.unitNumber}`,
    },
    {
      key: 'accountLevel',
      label: 'Account Level',
      children: rowData.accountLevel,
    },
    {
      key: 'contactNumber',
      label: 'Contact Number',
      children: rowData.contactNumber,
    },
    {
      key: 'email',
      label: 'Email Address',
      span: 'filled',
      children: rowData.email,
    },
    {
      key: 'dateTime',
      label: 'Requested On',
      span: 'filled',
      children: rowData.dateTime,
    },
    {
      key: 'documents',
      label: 'Documents',
      span: 'filled',
      children: renderDocuments(rowData.documents),
    },
    {
      // why a random colon appears here raaaaaaa
      key: 'emailExists',
      label: '',
      children: emailExists ? (
        <Text strong type="danger">
          An account with this email address already exists
        </Text>
      ) : (
        ''
      ),
    },
  ];

  useEffect(() => {
    const unsub = subscribeRequests(filterDate, filterOption, id, pendings);
    return () => unsub && unsub();
  }, [subscribeRequests, filterDate, filterOption, id, pendings]);

  return (
    <>
      <SEO
        description="Manage account requests"
        name="qiwei"
        title={`(${badgeCount}) Account Requests`}
        type="webapp"
      />
      <div>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="..">
                  <HomeOutlined className="dropdown-icon-margin" />
                  <span>Home</span>
                </Link>
              ),
            },
            {
              title: <span>Account Requests</span>,
            },
          ]}
        />
        <Flex align="center" wrap>
          <RangePicker
            format={dateFormat}
            id="rangePicker"
            name="rangePicker"
            onChange={handleDatePickerChange}
            showNow
            variant="borderless"
          />
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={handleReload}
            type="link"
          >
            Reload
          </Button>
          <Radio.Group
            buttonStyle="solid"
            defaultValue={pendings}
            id="radioGroup"
            name="statusFilter"
            onChange={(e) => setFilterOption(e.target.value)}
          >
            <Radio.Button value={pendings}>
              {pendingsStr.toUpperCase()}
            </Radio.Button>
            <Radio.Button value={approved}>
              {approvedStr.toUpperCase()}
            </Radio.Button>
            <Radio.Button value={rejected}>
              {rejectedStr.toUpperCase()}
            </Radio.Button>
          </Radio.Group>
        </Flex>
      </div>
      {contextHolder}
      <Table
        className="table margin"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={tableParams}
        rowKey="requestID"
        rowSelection={{
          align: 'center',
          columnWidth: 40,
          onChange: (selectedRowKeys, selectedRows) => {
            // console.log(selectedRowKeys, selectedRows[0]);
            setButtonStatus(selectedRows[0].requestStatus);
            handleEmailCheck(selectedRows[0].email, id);
            setIsModalOpen(true);
            setRowKeyData(selectedRows[0]);
            setRowKeySelected(selectedRowKeys);
          },
          selectedRowKeys: rowKeySelected,
          type: 'radio',
        }}
        scroll={{ x: 1120, y: scrollHeight }}
      />
      {Object.keys(rowKeyData ?? {}).length > 0 && (
        <Modal
          footer={[
            <Button key="cancel" onClick={handleCancel}>
              Cancel
            </Button>,
            <Button
              disabled={buttonStatus !== pendings} // info-request only from pending
              key="info"
              onClick={handleRequestInfo}
              type="default"
            >
              Request More Info
            </Button>,
            <Button
              danger
              disabled={buttonStatus !== pendings} // only enabled for pending
              icon={<CloseOutlined />}
              key="reject"
              onClick={handleRequestReject}
              type="default"
            >
              Reject
            </Button>,
            <Button
              disabled={buttonStatus !== pendings || emailExists} // also check email
              icon={<CheckOutlined />}
              key="approve"
              loading={loading}
              onClick={handleRequestApprove}
              type="primary"
            >
              Approve
            </Button>,
          ]}
          onCancel={handleCancel}
          open={isModalOpen}
          title={`Account Request [ID: ${rowKeyData.requestID}]`}
          width={normal}
        >
          <Descriptions
            bordered={false}
            colon
            column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
            items={requestItems(rowKeyData)}
            layout="horizontal"
            title={' '}
          />
        </Modal>
      )}
      <Modal
        cancelText="Cancel"
        confirmLoading={loading}
        okButtonProps={{ danger: true }}
        okText="Reject Request"
        onCancel={handleRejectCancel}
        onOk={handleRejectConfirm}
        open={rejectModalOpen}
        title="Reject Account Request"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Optionally provide a reason. The applicant may see this when checking
          their request status.
        </Text>
        <Input.TextArea
          autoSize={{ minRows: 3, maxRows: 6 }}
          maxLength={500}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="e.g., Block/unit does not match our records. Please re-submit with the correct details."
          showCount
          value={rejectReason}
        />
      </Modal>
      <Modal
        cancelText="Cancel"
        confirmLoading={loading}
        okText="Send Info Request"
        onCancel={handleInfoCancel}
        onOk={handleInfoConfirm}
        open={infoModalOpen}
        title="Request More Info"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Describe exactly what additional info or documents the applicant
          should provide. They&apos;ll receive this as an email.
        </Text>
        <Input.TextArea
          autoSize={{ minRows: 3, maxRows: 6 }}
          maxLength={500}
          onChange={(e) => setInfoReason(e.target.value)}
          placeholder="e.g., Property title doesn't list your name. Please upload the sale and purchase agreement."
          showCount
          value={infoReason}
        />
      </Modal>
    </>
  );
}

AccountRequests.propTypes = {
  badgeCount: PropTypes.number.isRequired,
  site: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
  updateRequestCount: PropTypes.func.isRequired,
};
